import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Lightweight client-side bot/abuse heuristics for username search inputs.
 *
 * Signals we look at:
 *  - Was there ANY real mouse / touch / keyboard activity on the page before submit?
 *    (headless puppeteer / fetch loops never fire those events)
 *  - Did the user actually TYPE in the input, or did the value just appear (paste-only / scripted)?
 *  - Typing speed — humans cap around ~12 chars/sec. >25 chars/sec sustained = bot.
 *  - Submission rate — more than N searches per rolling minute from the same browser session.
 *
 * When `suspicious` flips true, the consumer should require a CAPTCHA before allowing
 * the action (navigate / API call). Solving the CAPTCHA calls `clear()` to reset.
 */

const SESSION_KEY = "tdd_search_submits_v1";
const MAX_PER_MINUTE = 6;

// Module-level activity tracker — shared across every input on the page so we
// only need a single set of listeners.
let humanActivity = false;
let listenersAttached = false;

const ensureGlobalListeners = () => {
  if (listenersAttached || typeof window === "undefined") return;
  listenersAttached = true;
  const mark = () => {
    humanActivity = true;
  };
  // `once: true` keeps overhead at zero after the first real interaction.
  window.addEventListener("mousemove", mark, { once: true, passive: true });
  window.addEventListener("touchstart", mark, { once: true, passive: true });
  window.addEventListener("keydown", mark, { once: true, passive: true });
  window.addEventListener("pointerdown", mark, { once: true, passive: true });
  window.addEventListener("wheel", mark, { once: true, passive: true });
};

const readSubmits = (): number[] => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    const cutoff = Date.now() - 60_000;
    return arr.filter((t: unknown) => typeof t === "number" && t > cutoff);
  } catch {
    return [];
  }
};

const writeSubmits = (arr: number[]) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(arr));
  } catch {
    /* ignore */
  }
};

export interface BotDetection {
  /** True when current behavior looks bot-like and a CAPTCHA should be required. */
  suspicious: boolean;
  /** Call on every keystroke / input change. Tracks typing cadence. */
  noteInput: (newValue: string) => void;
  /** Call when input is focused (proves a focus event fired). */
  noteFocus: () => void;
  /** Call right before submit / navigation. Returns true if allowed, false if CAPTCHA needed. */
  checkSubmit: () => boolean;
  /** Called by the CAPTCHA component after a correct answer. */
  clear: () => void;
}

export const useBotDetection = (): BotDetection => {
  const [suspicious, setSuspicious] = useState(false);
  const focusedRef = useRef(false);
  const keystrokesRef = useRef(0);
  const firstKeyAtRef = useRef<number | null>(null);
  const lastValueRef = useRef("");
  // Count how many times the value jumped by >1 char (paste events). One paste
  // is normal — mobile users routinely copy a username from another app and
  // paste it. Multiple pastes in a row without any typing = scripted.
  const pasteCountRef = useRef(0);
  // After CAPTCHA is solved, give the user a short grace window so a single
  // search submission isn't blocked twice in a row.
  const trustedUntilRef = useRef(0);

  useEffect(() => {
    ensureGlobalListeners();
  }, []);

  const noteFocus = useCallback(() => {
    focusedRef.current = true;
  }, []);

  const noteInput = useCallback((newValue: string) => {
    const prev = lastValueRef.current;
    const delta = newValue.length - prev.length;
    lastValueRef.current = newValue;

    // Single-char delta = real keystroke.
    if (delta === 1 || delta === -1) {
      keystrokesRef.current += 1;
      if (firstKeyAtRef.current == null) firstKeyAtRef.current = Date.now();
    } else if (delta > 1) {
      // Big positive jump = paste. First paste is allowed silently.
      pasteCountRef.current += 1;
    }

    // Compute typing speed only after enough keystrokes.
    if (keystrokesRef.current >= 6 && firstKeyAtRef.current) {
      const elapsedSec = (Date.now() - firstKeyAtRef.current) / 1000;
      const cps = keystrokesRef.current / Math.max(elapsedSec, 0.001);
      if (cps > 25) {
        setSuspicious(true);
      }
    }
  }, []);

  const checkSubmit = useCallback((): boolean => {
    if (Date.now() < trustedUntilRef.current) return true;

    // Record this submission for rate analysis.
    const submits = readSubmits();
    submits.push(Date.now());
    writeSubmits(submits);

    let bad = false;
    // 1. Too many submits in the last minute.
    if (submits.length > MAX_PER_MINUTE) bad = true;
    // 2. No human activity on page at all.
    if (!humanActivity) bad = true;
    // 3. Submitted but never focused the input — value was set programmatically.
    if (!focusedRef.current) bad = true;
    // 4. Value present, no real keystrokes, AND no paste either — pure scripted set.
    //    A single paste is allowed (common mobile UX: copy username, paste, search).
    if (
      lastValueRef.current.length >= 2 &&
      keystrokesRef.current === 0 &&
      pasteCountRef.current === 0
    ) {
      bad = true;
    }
    // 5. Repeated paste-only behavior with zero typing across many submits =
    //    likely a script feeding usernames from a list.
    if (
      keystrokesRef.current === 0 &&
      pasteCountRef.current > 3 &&
      submits.length >= 4
    ) {
      bad = true;
    }

    if (bad) {
      setSuspicious(true);
      return false;
    }
    return true;
  }, []);

  const clear = useCallback(() => {
    setSuspicious(false);
    keystrokesRef.current = 0;
    firstKeyAtRef.current = null;
    pasteCountRef.current = 0;
    // Trust this session for 5 minutes after solving a CAPTCHA.
    trustedUntilRef.current = Date.now() + 5 * 60_000;
    // Wipe the rate window so they can keep searching normally.
    writeSubmits([]);
  }, []);

  return { suspicious, noteInput, noteFocus, checkSubmit, clear };
};
